import type { LocateSkill } from '@sb/ai/types';
import type { RoutineData } from '@sb/feathers-types';
import { makeNamespacedLog } from '@sb/log';
import type { Routine } from '@sb/remote-control/types';
import { Space } from '@sb/routine-runner';

import { convertRoutineSummary } from './convertRoutineSummary';
import { convertStepConfigurations } from './convertStepConfigurations';
import { convertStepSummary } from './convertStepSummary';
import { getVirtualSpaceItems } from './virtualSpaceItems';

const log = makeNamespacedLog('convertRoutineResponse');

export interface ConvertRoutineResponseOptions {
  getLocateSkillById?: (id: string) => LocateSkill | undefined;
}

export function convertRoutineResponse(
  data: RoutineData,
  options?: ConvertRoutineResponseOptions,
): Routine.ConvertedResponse {
  const summary = convertRoutineSummary(data);

  const steps = convertStepSummary(data.steps);

  const stepConfigurations = convertStepConfigurations(
    data.id,
    steps,
    data.stepConfigurations,
  );

  const space: Space.Item[] = [];

  if (data.space) {
    for (const spaceItem of data.space) {
      // Apply migration for backward compatibility
      const migratedItem = Space.migratePalletBoxesData(spaceItem);
      const parsedItem = Space.Item.safeParse(migratedItem);

      if (parsedItem.success) {
        space.push(parsedItem.data);
      } else {
        log.warn(
          'spaceItem.parse.error',
          'Failed to parse space item, skipping',
          {
            error: parsedItem.error,
            spaceItem,
          },
        );
      }
    }
  }

  space.push(
    ...getVirtualSpaceItems(stepConfigurations, options?.getLocateSkillById),
  );

  const routine: Routine.ConvertedResponse = {
    ...summary,
    configurationUpdatedAt: new Date(
      data.configurationUpdatedAt ?? data.createdAt,
    ),
    currentEditorID: null,
    environmentVariables: (data.environmentVariables ?? []).map((v) => ({
      ...v,
      persistent: v.persistent ?? false,
    })),
    isEphemeral: data.isEphemeral ?? false,
    isHidden: data.isHidden ?? false,
    mainLoopStepID: data.mainLoopStepID ?? null,
    space,
    steps,
    stepConfigurations,
  };

  return routine;
}
